import React from 'react';

export type AnnualTimetableLeaderImportSourceCandidate = {
  gradeId: string;
  rawName: string;
  title: string;
  grades: number[];
};

export type AnnualTimetableLeaderImportMode = 'annual_only' | 'annual_all';

type AnnualTimetableLeaderImportSourcePreviewProps = {
  candidates: AnnualTimetableLeaderImportSourceCandidate[];
  selectedGradeId: string;
  importMode: AnnualTimetableLeaderImportMode;
};

const MINI_WEEKDAY_COUNT = 3;
const MINI_PERIOD_COUNT = 2;
const MINI_TRACK_COUNT = 4;
const MINI_WEEK_ROW_COUNT = 6;
const SCHEDULE_ROW_COUNT = 8;
const SCHEDULE_WEEKDAY_COUNT = 5;
const SCHEDULE_LEFT_COLS = 6;
const GRADE_TRACK_COL_COUNT = 9;

function OutlineBox({ className = '' }: { className?: string }) {
  return <div className={'cp-annual-team-import-outline-box' + (className ? ' ' + className : '')} aria-hidden />;
}

function PeriodDot({ checked = false }: { checked?: boolean }) {
  return (
    <span
      className={'cp-annual-team-import-period-dot' + (checked ? ' is-checked' : '')}
      aria-hidden
    />
  );
}

function isSchedulePeriodChecked(rowIndex: number, dayIndex: number): boolean {
  if (rowIndex >= 6) return false;
  return dayIndex < SCHEDULE_WEEKDAY_COUNT;
}

function SkeletonBar({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  return (
    <span
      className={'cp-annual-team-import-skeleton-bar cp-annual-team-import-skeleton-bar--' + size}
      aria-hidden
    />
  );
}

function SkeletonCell({
  as: Tag = 'td',
  className = '',
  barSize = 'md',
}: {
  as?: 'td' | 'th';
  className?: string;
  barSize?: 'sm' | 'md' | 'lg';
}) {
  return (
    <Tag className={className}>
      <SkeletonBar size={barSize} />
    </Tag>
  );
}

function ClassBlock({ title, grades }: { title: string; grades: number[] }) {
  return (
    <div className="cp-grade-track-class-block">
      <span className="cp-grade-track-class-block-label">{title}</span>
      {grades.map((num) => (
        <span key={num} className="class-grade-chip-display">
          {num}
        </span>
      ))}
    </div>
  );
}

function PreviewBlockHeader({
  classScope,
  title,
  classTitle,
  classGrades,
  className = '',
}: {
  classScope: 'shared' | 'solo';
  title: string;
  classTitle: string;
  classGrades: number[];
  className?: string;
}) {
  return (
    <div
      className={
        'cp-track-section-header cp-track-section-header-with-class cp-grade-track-header-row cp-annual-team-import-block-header' +
        (className ? ' ' + className : '')
      }
    >
      <div className="card-summary pill cp-track-class-pill">
        {classScope === 'shared' ? '클래스 공유' : '클래스 단독'}
      </div>
      <h3 className="cp-annual-team-import-block-title">{title}</h3>
      <ClassBlock title={classTitle} grades={classGrades} />
    </div>
  );
}

function SideSectionShell({
  highlighted,
  children,
}: {
  highlighted: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={
        'cp-annual-team-import-side-shell' + (highlighted ? ' is-import-highlight' : '')
      }
    >
      {children}
    </div>
  );
}

function AcademicCalendarSidePreview({
  highlighted,
  classTitle,
  classGrades,
}: {
  highlighted: boolean;
  classTitle: string;
  classGrades: number[];
}) {
  return (
    <SideSectionShell highlighted={highlighted}>
      <div className="cp-academic-calendar-group cp-annual-team-import-inner-section">
        <PreviewBlockHeader
          classScope="shared"
          title="학사일정 수업일 설정"
          classTitle={classTitle}
          classGrades={classGrades}
        />
        <div className="cp-section-body cp-annual-team-import-academic-body">
          <div className="cp-annual-team-import-academic-shape" aria-hidden>
            <div className="cp-annual-team-import-academic-shape-two-col">
              <div className="cp-annual-team-import-academic-shape-card is-settings is-chunk-only" />
              <div className="cp-annual-team-import-academic-shape-right">
                <div className="cp-annual-team-import-academic-shape-card is-holiday is-chunk-only" />
                <div className="cp-annual-team-import-academic-shape-card is-weekend is-chunk-only" />
              </div>
            </div>
            <div className="cp-annual-team-import-academic-shape-summary">
              <div className="cp-annual-team-import-academic-shape-card is-summary is-chunk-only" />
            </div>
          </div>
        </div>
      </div>
    </SideSectionShell>
  );
}

function ClassScheduleSidePreview({
  highlighted,
  classTitle,
  classGrades,
}: {
  highlighted: boolean;
  classTitle: string;
  classGrades: number[];
}) {
  return (
    <SideSectionShell highlighted={highlighted}>
      <article className="cp-card cp-annual-team-import-inner-section">
        <PreviewBlockHeader
          classScope="shared"
          title="수업시간 및 요일별 기준 수업시수 설정"
          classTitle={classTitle}
          classGrades={classGrades}
          className="cp-class-schedule-section-header"
        />
        <div className="cp-section-body">
          <div className="cp-class-schedule-table-wrap">
            <table className="cp-class-schedule-table cp-class-schedule-unified-table cp-annual-team-import-schedule-table">
              <thead>
                <tr>
                  <th colSpan={SCHEDULE_LEFT_COLS} className="cp-period-table-title-cell" aria-hidden />
                  <th
                    colSpan={SCHEDULE_WEEKDAY_COUNT + 1}
                    className="cp-period-table-divider cp-period-table-title-cell"
                    aria-hidden
                  />
                </tr>
                <tr>
                  {Array.from({ length: SCHEDULE_LEFT_COLS + SCHEDULE_WEEKDAY_COUNT + 1 }).map((_, colIndex) => (
                    <th
                      key={colIndex}
                      className={colIndex === SCHEDULE_LEFT_COLS ? 'cp-period-table-divider' : undefined}
                      aria-hidden
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: SCHEDULE_ROW_COUNT }).map((_, rowIndex) => (
                  <tr
                    key={rowIndex}
                    className={rowIndex >= 6 ? 'cp-annual-team-import-schedule-row-muted' : undefined}
                  >
                    {Array.from({ length: SCHEDULE_LEFT_COLS }).map((__, colIndex) => (
                      <td key={colIndex}>
                        <OutlineBox className="is-cell" />
                      </td>
                    ))}
                    <td className="cp-period-table-divider">
                      <OutlineBox className="is-cell is-narrow" />
                    </td>
                    {Array.from({ length: SCHEDULE_WEEKDAY_COUNT }).map((__, dayIndex) => (
                      <td key={dayIndex}>
                        <PeriodDot checked={isSchedulePeriodChecked(rowIndex, dayIndex)} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </article>
    </SideSectionShell>
  );
}

function GradeTrackSidePreview({
  highlighted,
  classTitle,
  classGrades,
}: {
  highlighted: boolean;
  classTitle: string;
  classGrades: number[];
}) {
  return (
    <SideSectionShell highlighted={highlighted}>
      <article className="cp-card cp-annual-team-import-inner-section">
        <PreviewBlockHeader
          classScope="solo"
          title="편제 및 시간표 입력 양식"
          classTitle={classTitle}
          classGrades={classGrades}
        />
        <div className="cp-section-body">
          <div className="cp-annual-team-import-track-shape" aria-hidden>
            <div className="cp-annual-team-import-track-label-row">
              <OutlineBox className="is-label" />
              <OutlineBox className="is-label" />
              <OutlineBox className="is-track-btn" />
            </div>
            <div className="cp-annual-team-import-track-cols-row">
              {Array.from({ length: GRADE_TRACK_COL_COUNT }).map((_, index) => (
                <div key={index} className="cp-annual-team-import-track-shape-col">
                  <div className="cp-annual-team-import-track-shape-col-top">
                    <OutlineBox className="is-icon" />
                    <OutlineBox className="is-icon" />
                  </div>
                  <OutlineBox className="is-chip" />
                  <OutlineBox className="is-name" />
                  <OutlineBox className="is-body" />
                  <OutlineBox className="is-hours" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </article>
    </SideSectionShell>
  );
}

function MiniTermTable({ termKey }: { termKey: '1' | '2' }) {
  const termLabel = termKey === '1' ? '1학기 연간 시간표' : '2학기 연간 시간표';

  return (
    <div className="cp-annual-team-import-mini-term-block">
      <h4 className="cp-annual-team-import-mini-term-title">{termLabel}</h4>
      <div className="cp-annual-timetable-table-wrap cp-annual-team-import-table-wrap">
        <table
          className={
            'cp-weekly-schedule-table cp-annual-timetable-fixed-table cp-annual-team-import-skeleton-table ' +
            (termKey === '1' ? 'cp-annual-term-table-term1' : 'cp-annual-term-table-term2')
          }
        >
          <colgroup>
            <col className="cp-annual-team-import-col-meta" />
            <col className="cp-annual-team-import-col-meta" />
            <col className="cp-annual-team-import-col-date" />
            {Array.from({ length: MINI_WEEKDAY_COUNT * MINI_PERIOD_COUNT }).map((_, index) => (
              <col key={`period-col-${index}`} className="cp-annual-team-import-col-period" />
            ))}
            <col className="cp-annual-team-import-col-meta" />
          </colgroup>
          <thead>
            <tr>
              <SkeletonCell as="th" className="cp-weekly-schedule-th-month" barSize="sm" />
              <SkeletonCell as="th" className="cp-weekly-schedule-th-week" barSize="sm" />
              <SkeletonCell as="th" className="cp-weekly-schedule-th-daterange" barSize="md" />
              {Array.from({ length: MINI_WEEKDAY_COUNT }).map((_, dayIndex) => (
                <th
                  key={`weekday-${dayIndex}`}
                  colSpan={MINI_PERIOD_COUNT}
                  className="cp-weekly-schedule-th-weekday"
                >
                  <SkeletonBar size="sm" />
                </th>
              ))}
              <SkeletonCell as="th" className="cp-weekly-schedule-th-hours" barSize="sm" />
            </tr>
            <tr>
              <th
                colSpan={3}
                className="cp-weekly-schedule-th-daterange cp-annual-selected-date-headcell"
              >
                <SkeletonBar size="md" />
              </th>
              {Array.from({ length: MINI_WEEKDAY_COUNT * MINI_PERIOD_COUNT }).map((_, index) => (
                <SkeletonCell
                  key={`period-${index}`}
                  as="th"
                  className="cp-weekly-schedule-th-period cp-annual-period-head-th"
                  barSize="sm"
                />
              ))}
              <SkeletonCell
                as="th"
                className="cp-weekly-schedule-th-hours cp-weekly-schedule-semester-hours"
                barSize="sm"
              />
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: MINI_WEEK_ROW_COUNT }).map((_, weekIndex) => (
              <tr key={weekIndex}>
                <SkeletonCell as="td" className="cp-weekly-schedule-td-month" barSize="sm" />
                <SkeletonCell as="td" className="cp-weekly-schedule-td-week" barSize="sm" />
                <SkeletonCell
                  as="td"
                  className="cp-weekly-schedule-td-daterange cp-annual-week-memo-anchor-cell"
                  barSize="md"
                />
                {Array.from({ length: MINI_WEEKDAY_COUNT * MINI_PERIOD_COUNT }).map((_, cellIndex) => (
                  <td key={cellIndex} className="cp-weekly-schedule-cell">
                    <div className="cp-base-timetable-cell-inner cp-annual-timetable-cell-inner">
                      <SkeletonBar size="md" />
                    </div>
                  </td>
                ))}
                <SkeletonCell as="td" className="cp-weekly-schedule-td-hours" barSize="sm" />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MiniSummaryTable() {
  return (
    <div className="cp-annual-term-summary-block cp-annual-team-import-mini-summary-block">
      <div className="cp-annual-timetable-table-wrap cp-annual-team-import-table-wrap">
        <table className="cp-annual-term-summary-table cp-annual-team-import-skeleton-table">
          <colgroup>
            <col className="cp-annual-team-import-col-summary-head" />
            {Array.from({ length: MINI_TRACK_COUNT }).map((_, index) => (
              <col key={`summary-col-${index}`} className="cp-annual-team-import-col-summary-track" />
            ))}
          </colgroup>
          <tbody>
            <tr className="cp-annual-term-summary-row-shaded">
              <SkeletonCell as="th" className="cp-annual-term-summary-rowhead" barSize="lg" />
              {Array.from({ length: MINI_TRACK_COUNT }).map((_, index) => (
                <SkeletonCell
                  key={`track-${index}`}
                  className="cp-annual-term-summary-cell cp-annual-term-summary-trackhead"
                  barSize="lg"
                />
              ))}
            </tr>
            <tr>
              <SkeletonCell as="th" className="cp-annual-term-summary-rowhead" barSize="lg" />
              {Array.from({ length: MINI_TRACK_COUNT }).map((_, index) => (
                <SkeletonCell
                  key={`baseline-${index}`}
                  className="cp-annual-term-summary-cell cp-annual-term-summary-baseline"
                  barSize="md"
                />
              ))}
            </tr>
            <tr className="cp-annual-summary-hours-row-term1 cp-annual-summary-hours-row-track">
              <SkeletonCell as="th" className="cp-annual-term-summary-rowhead" barSize="lg" />
              {Array.from({ length: MINI_TRACK_COUNT }).map((_, index) => (
                <SkeletonCell key={`term1-${index}`} className="cp-annual-term-summary-cell" barSize="md" />
              ))}
            </tr>
            <tr className="cp-annual-summary-hours-row-term2 cp-annual-summary-hours-row-track">
              <SkeletonCell as="th" className="cp-annual-term-summary-rowhead" barSize="lg" />
              {Array.from({ length: MINI_TRACK_COUNT }).map((_, index) => (
                <SkeletonCell key={`term2-${index}`} className="cp-annual-term-summary-cell" barSize="md" />
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

const AnnualTimetableLeaderImportSourcePreview: React.FC<
  AnnualTimetableLeaderImportSourcePreviewProps
> = ({ candidates, selectedGradeId, importMode }) => {
  const selectedCandidate =
    candidates.find((row) => row.gradeId === selectedGradeId) ?? candidates[0] ?? null;

  if (!selectedCandidate) return null;

  const highlightSideSections = importMode === 'annual_all';
  const highlightAnnual = true;

  return (
    <div className="cp-annual-team-import-preview-layout">
      <div className="cp-annual-team-import-side-previews">
        <AcademicCalendarSidePreview
          highlighted={highlightSideSections}
          classTitle={selectedCandidate.title}
          classGrades={selectedCandidate.grades}
        />
        <ClassScheduleSidePreview
          highlighted={highlightSideSections}
          classTitle={selectedCandidate.title}
          classGrades={selectedCandidate.grades}
        />
        <GradeTrackSidePreview
          highlighted={highlightSideSections}
          classTitle={selectedCandidate.title}
          classGrades={selectedCandidate.grades}
        />
      </div>
      <div
        className={
          'cp-annual-team-import-source-preview cp-annual-team-import-annual-panel' +
          (highlightAnnual ? ' is-import-highlight' : '')
        }
        aria-label={`${selectedCandidate.title} 연간시간표 미리보기`}
      >
        <PreviewBlockHeader
          classScope="solo"
          title={`${selectedCandidate.title} 연간시간표`}
          classTitle={selectedCandidate.title}
          classGrades={selectedCandidate.grades}
          className="cp-annual-team-import-source-preview-head"
        />
        <div className="cp-annual-team-import-source-preview-tables" aria-hidden="true">
          <MiniTermTable termKey="1" />
          <MiniSummaryTable />
          <MiniTermTable termKey="2" />
        </div>
      </div>
    </div>
  );
};

export default AnnualTimetableLeaderImportSourcePreview;
